import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Reveal } from '../Reveal';
import {
  ArrowLeft, ArrowRight, Clock, ChevronRight, User, Share2, Linkedin,
  CheckCircle2, ShieldCheck, Zap, AlertTriangle, BarChart3,
  Lock, Cpu, Mail, Send, Globe, TrendingUp, Award, Timer, BookOpen, Lightbulb, Shield, Check, Info, Target, TrendingDown, HelpCircle, HardHat, Scale, Building, PenTool, Activity, FileText
} from 'lucide-react';
import { Page } from '../../types';

interface Article {
  id: string;
  number: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  image: string;
  intro: string; // Exactly 40 words
  content: React.ReactNode;
}

// --- Article Content Components ---

const renderWithBold = (text: any) => {
  if (typeof text !== 'string') return text || '';
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index} className="font-bold text-gray-900">{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

const Article01Content: React.FC<{ t: any, onNavigate?: (page: any) => void }> = ({ t, onNavigate }) => (
  <div className="space-y-16 text-gray-800 leading-relaxed text-lg font-light max-w-4xl mx-auto pb-20 selection:bg-clever-teal selection:text-white">
    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s1.title')}</h2>
      <p>{t('shared.articles.list.ART_01.sections.s1.p1')}</p>
      <p>{renderWithBold(t('shared.articles.list.ART_01.sections.s1.p2'))}</p>
      <p>{t('shared.articles.list.ART_01.sections.s1.p3')}</p>
    </section>

    <div className="bg-gray-50 p-12 border-l-4 border-clever-teal my-16 shadow-inner relative overflow-hidden">
      <div className="absolute top-0 right-0 p-4 opacity-5"><TrendingDown size={120} /></div>
      <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-3 text-2xl tracking-tighter"><Activity size={28} className="text-clever-teal" /> {t('shared.articles.list.ART_01.sections.dive.title')}</h4>
      <p className="leading-relaxed italic text-gray-600 mb-8">
        {renderWithBold(t('shared.articles.list.ART_01.sections.dive.quote'))}
      </p>
    </div>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s2.title')}</h2>
      <p>{t('shared.articles.list.ART_01.sections.s2.p1')}</p>
      <p>{t('shared.articles.list.ART_01.sections.s2.p2')}</p>

      <div className="grid md:grid-cols-2 gap-12 my-20">
        <div className="p-10 bg-[#0B1121] text-white rounded-sm">
          <h4 className="font-bold mb-6 text-clever-teal uppercase text-[11px] tracking-[0.3em]">{t('shared.articles.list.ART_01.sections.s2.box_title')}</h4>
          <p className="text-lg opacity-80 font-light leading-relaxed">{t('shared.articles.list.ART_01.sections.s2.box_text')}</p>
        </div>
        <div className="p-10 bg-gray-100 text-gray-900 rounded-sm border-l-4 border-gray-900">
          <h4 className="font-bold mb-6 text-gray-500 uppercase text-[11px] tracking-[0.3em]">{t('shared.articles.list.ART_01.sections.s2.case_title')}</h4>
          <p className="text-lg opacity-80 font-light leading-relaxed">{t('shared.articles.list.ART_01.sections.s2.case_text')}</p>
        </div>
      </div>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-none border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s3.title')}</h2>
      <p>{t('shared.articles.list.ART_01.sections.s3.intro')}</p>
      <div className="space-y-12 mt-12 relative pl-8 border-l border-gray-100">
        {[
          { t: t('shared.articles.list.ART_01.sections.s3.step1_title'), d: t('shared.articles.list.ART_01.sections.s3.step1_text') },
          { t: t('shared.articles.list.ART_01.sections.s3.step2_title'), d: t('shared.articles.list.ART_01.sections.s3.step2_text') },
          { t: t('shared.articles.list.ART_01.sections.s3.step3_title'), d: t('shared.articles.list.ART_01.sections.s3.step3_text') },
          { t: t('shared.articles.list.ART_01.sections.s3.step4_title'), d: t('shared.articles.list.ART_01.sections.s3.step4_text') },
          { t: t('shared.articles.list.ART_01.sections.s3.step5_title'), d: t('shared.articles.list.ART_01.sections.s3.step5_text') }
        ].map((step, i) => (
          <div key={i} className="relative group">
            <div className="absolute -left-[41px] top-0 w-5 h-5 rounded-full bg-white border-2 border-gray-300 group-hover:border-clever-teal transition-colors shadow-sm"></div>
            <h4 className="font-bold text-gray-900 mb-2 text-2xl tracking-tight leading-none">{i + 1}. {step.t}</h4>
            <p className="text-gray-500 font-light text-base">{step.d}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s4.title')}</h2>
      <p>{t('shared.articles.list.ART_01.sections.s4.p1')}</p>
      <p>{renderWithBold(t('shared.articles.list.ART_01.sections.s4.p2'))}</p>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s5.title')}</h2>
      <p>{t('shared.articles.list.ART_01.sections.s5.p1')}</p>
      <p>{t('shared.articles.list.ART_01.sections.s5.p2')}</p>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_01.sections.s6.title')}</h2>
      <p>{renderWithBold(t('shared.articles.list.ART_01.sections.s6.p1'))}</p>
      <p>{t('shared.articles.list.ART_01.sections.s6.p2')}</p>
    </section>

    <section className="mt-32 p-16 bg-[#0B1121] text-white rounded-sm relative overflow-hidden shadow-2xl">
      <div className="absolute top-0 right-0 w-96 h-96 bg-clever-teal/5 rounded-full blur-[150px] pointer-events-none"></div>
      <h3 className="text-4xl font-bold mb-8 tracking-tighter leading-none text-white">{t('shared.articles.list.ART_01.sections.cta.title')}</h3>
      <p className="text-gray-400 mb-12 max-w-2xl text-lg leading-relaxed">{t('shared.articles.list.ART_01.sections.cta.text')}</p>
      <div className="flex flex-col sm:flex-row gap-6">
        <button onClick={() => onNavigate && onNavigate('contact')} className="bg-white text-[#0B1121] px-12 py-5 font-bold uppercase tracking-widest text-xs hover:bg-clever-teal hover:text-white transition-all shadow-xl rounded-sm flex items-center gap-3">{t('shared.articles.list.ART_01.sections.cta.button_audit')} <ArrowRight size={16} /></button>
        <button onClick={() => onNavigate && onNavigate('roi')} className="bg-transparent text-white border border-white/20 px-12 py-5 font-bold uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-all rounded-sm">{t('shared.articles.list.ART_01.sections.cta.button_roi')}</button>
      </div>
    </section>
  </div>
);

const Article02Content: React.FC<{ t: any, onNavigate?: (page: any) => void }> = ({ t, onNavigate }) => (
  <div className="space-y-16 text-gray-800 leading-relaxed text-xl font-light max-w-4xl mx-auto pb-20 selection:bg-clever-teal selection:text-white">
    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_02.sections.s1.title')}</h2>
      <p>{t('shared.articles.list.ART_02.sections.s1.p1')}</p>
      <p>{renderWithBold(t('shared.articles.list.ART_02.sections.s1.p2'))}</p>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_02.sections.s2.title')}</h2>
      <p>{t('shared.articles.list.ART_02.sections.s2.p1')}</p>
      <p>{t('shared.articles.list.ART_02.sections.s2.p2')}</p>
    </section>

    <section className="grid md:grid-cols-2 gap-12 my-20">
      <div className="p-10 bg-gray-50 border-l-4 border-clever-teal">
        <h4 className="font-bold text-gray-900 mb-4 text-xl">{t('shared.articles.list.ART_02.sections.box1.title')}</h4>
        <p className="text-base text-gray-600 font-light leading-relaxed">{t('shared.articles.list.ART_02.sections.box1.text')}</p>
      </div>
      <div className="p-10 bg-[#0B1121] text-white rounded-sm">
        <h4 className="font-bold mb-4 text-xl text-clever-teal">{t('shared.articles.list.ART_02.sections.box2.title')}</h4>
        <p className="text-base text-gray-400 font-light leading-relaxed">
          {renderWithBold(t('shared.articles.list.ART_02.sections.box2.text'))}
        </p>
      </div>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_02.sections.s3.title')}</h2>
      <p>{t('shared.articles.list.ART_02.sections.s3.p1')}</p>
      <p>{t('shared.articles.list.ART_02.sections.s3.p2')}</p>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_02.sections.s4.title')}</h2>
      <p>{t('shared.articles.list.ART_02.sections.s4.p1')}</p>
      <p>{renderWithBold(t('shared.articles.list.ART_02.sections.s4.p2'))}</p>
    </section>

    <section className="bg-gray-50 p-12 text-center rounded-sm border border-gray-100">
      <h3 className="text-3xl font-bold text-gray-900 mb-6">{t('shared.articles.list.ART_02.sections.cta.title')}</h3>
      <p className="mb-10 text-gray-500 max-w-2xl mx-auto">{t('shared.articles.list.ART_02.sections.cta.text')}</p>
      <button onClick={() => onNavigate && onNavigate('contact')} className="bg-[#0B1121] text-white px-12 py-5 font-bold uppercase tracking-widest text-xs hover:bg-clever-teal transition-all shadow-xl">{t('shared.articles.list.ART_02.sections.cta.button')}</button>
    </section>
  </div>
);

const Article03Content: React.FC<{ t: any }> = ({ t }) => (
  <div className="space-y-16 text-gray-800 leading-relaxed text-lg font-light max-w-4xl mx-auto pb-20 selection:bg-clever-teal selection:text-white">
    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">
        {t('shared.articles.list.ART_03.sections.s1.title')}
      </h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_03.sections.s1.p1'))}
      </p>
      <p>
        {t('shared.articles.list.ART_03.sections.s1.p2')}
      </p>
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_03.sections.s2.title')}</h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_03.sections.s2.p1'))}
      </p>
      <p>
        {t('shared.articles.list.ART_03.sections.s2.p2')}
      </p>
    </section>

    <div className="bg-gray-50 p-12 border border-gray-100 rounded-sm">
      <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-8 font-mono">{t('shared.articles.list.ART_03.sections.market_share.title')}</h4>
      <div className="space-y-8">
        {[
          { name: t('shared.articles.list.ART_03.sections.market_share.item1_name'), role: t('shared.articles.list.ART_03.sections.market_share.item1_role'), share: "32%", color: "bg-clever-teal" },
          { name: "Microsoft / Azure AI", role: t('shared.articles.list.ART_03.sections.market_share.item2_role'), share: "45%", color: "bg-gray-900" },
          { name: "Mistral AI (European Models)", role: t('shared.articles.list.ART_03.sections.market_share.item3_role'), share: "18%", color: "bg-gray-400" },
          { name: "Others", role: t('shared.articles.list.ART_03.sections.market_share.item4_role'), share: "5%", color: "bg-gray-200" }
        ].map((m, i) => (
          <div key={i}>
            <div className="flex justify-between mb-2 items-end">
              <div>
                <span className="font-bold text-gray-900">{m.name}</span>
                <span className="text-xs text-gray-400 ml-4 font-mono">{m.role}</span>
              </div>
              <span className="font-mono text-sm font-bold">{m.share}</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200">
              <div className={`h-full ${m.color}`} style={{ width: m.share }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_03.sections.s3.title')}</h2>
      <p>
        {t('shared.articles.list.ART_03.sections.s3.p1')}
      </p>
      <p>
        {renderWithBold(t('shared.articles.list.ART_03.sections.s3.p2'))}
      </p>
    </section>
  </div>
);

const Article04Content: React.FC<{ t: any, onNavigate?: (page: any) => void }> = ({ t, onNavigate }) => (
  <div className="space-y-16 text-gray-800 leading-relaxed text-lg font-light max-w-4xl mx-auto pb-20 selection:bg-clever-teal selection:text-white">
    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">
        {t('shared.articles.list.ART_04.sections.s1.title')}
      </h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_04.sections.s1.p1'))}
      </p>
      <p>
        {renderWithBold(t('shared.articles.list.ART_04.sections.s1.p2'))}
      </p>
    </section>

    <section className="space-y-12 my-20">
      {[
        { title: t('shared.articles.list.ART_04.sections.solutions.item1_title'), text: t('shared.articles.list.ART_04.sections.solutions.item1_text') },
        { title: t('shared.articles.list.ART_04.sections.solutions.item2_title'), text: t('shared.articles.list.ART_04.sections.solutions.item2_text') },
        { title: t('shared.articles.list.ART_04.sections.solutions.item3_title'), text: t('shared.articles.list.ART_04.sections.solutions.item3_text') }
      ].map((sol, i) => (
        <div key={i} className="flex gap-8 items-start p-10 bg-gray-50 border border-gray-100 group hover:border-clever-teal transition-all">
          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm text-clever-teal group-hover:bg-clever-teal group-hover:text-white transition-colors"><Zap size={24} /></div>
          <div>
            <h4 className="text-2xl font-bold mb-4 text-gray-900">{sol.title}</h4>
            <p className="text-gray-600 font-light leading-relaxed">{sol.text}</p>
          </div>
        </div>
      ))}
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_04.sections.s2.title')}</h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_04.sections.s2.p1'))}
      </p>
      <p>
        {t('shared.articles.list.ART_04.sections.s2.p2')}
      </p>
    </section>

    <footer className="mt-20 p-12 bg-[#0B1121] text-white rounded-sm relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-clever-teal/10 rounded-full blur-3xl"></div>
      <h3 className="text-2xl font-bold mb-6 text-white">{t('shared.articles.list.ART_04.sections.footer.title')}</h3>
      <p className="text-gray-400 mb-10 max-w-xl">{renderWithBold(t('shared.articles.list.ART_04.sections.footer.text'))}</p>
      <button onClick={() => onNavigate && onNavigate('roi')} className="bg-white text-black px-10 py-4 font-bold uppercase tracking-widest text-xs hover:bg-clever-teal hover:text-white transition-all">{t('shared.articles.list.ART_04.sections.footer.button')}</button>
    </footer>
  </div>
);

const Article05Content: React.FC<{ t: any }> = ({ t }) => (
  <div className="space-y-16 text-gray-800 leading-relaxed text-lg font-light max-w-4xl mx-auto pb-20 selection:bg-clever-teal selection:text-white">
    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">
        {t('shared.articles.list.ART_05.sections.s1.title')}
      </h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_05.sections.s1.p1'))}
      </p>
      <p>
        {renderWithBold(t('shared.articles.list.ART_05.sections.s1.p2'))}
      </p>
    </section>

    <section className="space-y-12 my-20">
      {[
        { name: "NVIDIA", desc: t('shared.articles.list.ART_05.sections.companies.item1_desc'), tag: "INFRASTRUKTUR" },
        { name: "OpenAI / MS", desc: t('shared.articles.list.ART_05.sections.companies.item2_desc'), tag: "MODELLE" },
        { name: "Google / Alphabet", desc: t('shared.articles.list.ART_05.sections.companies.item3_desc'), tag: "MULTIMODAL" },
        { name: "Meta (Facebook)", desc: t('shared.articles.list.ART_05.sections.companies.item4_desc'), tag: "OPEN SOURCE" },
        { name: "Anthropic", desc: t('shared.articles.list.ART_05.sections.companies.item5_desc'), tag: "SAFETY" },
        { name: "Mistral AI", desc: t('shared.articles.list.ART_05.sections.companies.item6_desc'), tag: "EUROPE" },
        { name: "Apple", desc: t('shared.articles.list.ART_05.sections.companies.item7_desc'), tag: "CONSUMER" }
      ].map((co, i) => (
        <div key={i} className="flex gap-10 items-start pb-12 border-b border-gray-100 group">
          <div className="text-6xl font-bold text-gray-100 group-hover:text-clever-teal transition-colors font-mono">0{i + 1}</div>
          <div>
            <h4 className="text-3xl font-bold text-gray-900 mb-3 uppercase tracking-tighter">{co.name}</h4>
            <p className="text-gray-500 mb-4 font-light text-base leading-relaxed">{co.desc}</p>
            <span className="text-[10px] font-bold uppercase tracking-widest text-clever-teal px-3 py-1 border border-clever-teal/20 rounded-full">{co.tag}</span>
          </div>
        </div>
      ))}
    </section>

    <section className="space-y-8">
      <h2 className="text-4xl font-bold text-gray-900 tracking-tighter leading-tight border-b border-gray-100 pb-6">{t('shared.articles.list.ART_05.sections.s2.title')}</h2>
      <p>
        {renderWithBold(t('shared.articles.list.ART_05.sections.s2.p1'))}
      </p>
      <p>
        {t('shared.articles.list.ART_05.sections.s2.p2')}
      </p>
    </section>
  </div>
);

export const ArticlesPage: React.FC<{ onNavigate?: (page: Page) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);

  useEffect(() => {
    setSelectedArticleId(null);
    window.scrollTo(0, 0);
  }, []);

  const handleSelectArticle = (articleId: string) => {
    setSelectedArticleId(articleId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const NewsletterBox = () => (
    <div className="mt-32 py-24 border-t border-gray-100 max-w-2xl mx-auto text-center">
      <div className="inline-block p-4 bg-gray-50 rounded-full mb-8">
        <Mail size={24} className="text-gray-400" />
      </div>
      <h4 className="text-[11px] font-bold uppercase tracking-[0.4em] text-gray-900 mb-6">{t('shared.articles.newsletter.title')}</h4>
      <p className="text-gray-500 text-base mb-12 font-light leading-relaxed">
        {t('shared.articles.newsletter.text')}
      </p>
      <form className="flex flex-col sm:flex-row gap-0 max-w-md mx-auto border border-gray-200 p-1 rounded-sm shadow-sm" onSubmit={(e) => e.preventDefault()}>
        <input
          type="email"
          placeholder={t('shared.articles.newsletter.placeholder')}
          className="flex-1 bg-white px-6 py-4 text-sm focus:outline-none placeholder:text-gray-300 font-light"
        />
        <button className="bg-[#0B1121] text-white px-10 py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-clever-teal transition-all whitespace-nowrap">
          {t('shared.articles.newsletter.cta')}
        </button>
      </form>
      <p className="text-[10px] text-gray-300 mt-8 uppercase tracking-widest">{t('shared.articles.newsletter.footer')}</p>
    </div>
  );

  const articles: Article[] = [
    {
      id: "ART_01",
      number: "01",
      title: t('shared.articles.list.ART_01.title'),
      excerpt: t('shared.articles.list.ART_01.excerpt'),
      category: t('shared.articles.list.ART_01.category'),
      readTime: t('shared.articles.list.ART_01.readTime'),
      date: t('shared.articles.list.ART_01.date'),
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=2070&auto=format&fit=crop",
      intro: t('shared.articles.list.ART_01.intro'),
      content: <Article01Content t={t} onNavigate={onNavigate} />
    },
    {
      id: "ART_02",
      number: "02",
      title: t('shared.articles.list.ART_02.title'),
      excerpt: t('shared.articles.list.ART_02.excerpt'),
      category: t('shared.articles.list.ART_02.category'),
      readTime: t('shared.articles.list.ART_02.readTime'),
      date: t('shared.articles.list.ART_02.date'),
      image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?q=80&w=2006&auto=format&fit=crop",
      intro: t('shared.articles.list.ART_02.intro'),
      content: <Article02Content t={t} onNavigate={onNavigate} />
    },
    {
      id: "ART_03",
      number: "03",
      title: t('shared.articles.list.ART_03.title'),
      excerpt: t('shared.articles.list.ART_03.excerpt'),
      category: t('shared.articles.list.ART_03.category'),
      readTime: t('shared.articles.list.ART_03.readTime'),
      date: t('shared.articles.list.ART_03.date'),
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2070&auto=format&fit=crop",
      intro: t('shared.articles.list.ART_03.intro'),
      content: <Article03Content t={t} />
    },
    {
      id: "ART_04",
      number: "04",
      title: t('shared.articles.list.ART_04.title'),
      excerpt: t('shared.articles.list.ART_04.excerpt'),
      category: t('shared.articles.list.ART_04.category'),
      readTime: t('shared.articles.list.ART_04.readTime'),
      date: t('shared.articles.list.ART_04.date'),
      image: "https://images.unsplash.com/photo-1555255707-c07966088b7b?q=80&w=2000&auto=format&fit=crop",
      intro: t('shared.articles.list.ART_04.intro'),
      content: <Article04Content t={t} onNavigate={onNavigate} />
    },
    {
      id: "ART_05",
      number: "05",
      title: t('shared.articles.list.ART_05.title'),
      excerpt: t('shared.articles.list.ART_05.excerpt'),
      category: t('shared.articles.list.ART_05.category'),
      readTime: t('shared.articles.list.ART_05.readTime'),
      date: t('shared.articles.list.ART_05.date'),
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2000&auto=format&fit=crop",
      intro: t('shared.articles.list.ART_05.intro'),
      content: <Article05Content t={t} />
    }
  ];

  const selectedArticle = selectedArticleId ? articles.find(a => a.id === selectedArticleId) : null;

  if (selectedArticle) {
    return (
      <div className="bg-white min-h-screen pt-24 font-sans text-gray-900 animate-fade-in">
        <article className="max-w-5xl mx-auto px-6 py-20">
          <button
            onClick={() => { setSelectedArticleId(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-clever-teal transition-colors mb-12 group"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" /> {t('shared.articles.navigation.back')}
          </button>

          <div className="mb-20">
            <div className="flex items-center gap-6 mb-10">
              <span className="text-[10px] font-bold font-mono bg-black text-white px-4 py-2 uppercase tracking-widest">{selectedArticle.category}</span>
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest border-l border-gray-200 pl-6">{selectedArticle.date}</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mb-12 max-w-4xl">
              {selectedArticle.title}
            </h1>

            <div className="mb-12 p-10 bg-gray-50 border-l-4 border-clever-teal italic text-xl text-gray-700 leading-relaxed font-light shadow-sm">
              {selectedArticle.intro}
            </div>

            <div className="flex items-center gap-10 border-y border-gray-100 py-8">
              <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                <User size={16} className="text-clever-teal" /> {t('shared.articles.navigation.author')}
              </div>
              <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">
                <Clock size={16} className="text-clever-teal" /> {selectedArticle.readTime} {t('shared.articles.navigation.read_time')}
              </div>
              <div className="ml-auto flex gap-4">
                <button className="p-3 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors shadow-sm" title="Teilen"><Share2 size={18} /></button>
                <button className="p-3 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors shadow-sm" title="LinkedIn"><Linkedin size={18} /></button>
              </div>
            </div>
          </div>

          <div className="aspect-[21/9] w-full bg-gray-100 mb-20 overflow-hidden rounded-sm grayscale hover:grayscale-0 transition-all duration-1000 shadow-2xl">
            <img src={selectedArticle.image} alt={selectedArticle.title} className="w-full h-full object-cover" />
          </div>

          <div className="prose prose-2xl prose-rabbit max-w-none">
            {selectedArticle.content}
          </div>

          <NewsletterBox />

          <footer className="mt-32 pt-20 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-12">
            <div className="max-w-md text-center md:text-left">
              <h4 className="text-2xl font-bold mb-4 tracking-tight">{t('shared.articles.footer.cta_title')}</h4>
              <p className="text-gray-500 font-light">{t('shared.articles.footer.cta_text')}</p>
            </div>
            <div className="flex gap-4 w-full md:w-auto">
              <button
                onClick={() => onNavigate && onNavigate('contact')}
                className="flex-1 md:flex-none bg-clever-teal text-white px-10 py-5 font-bold uppercase tracking-widest text-xs hover:bg-black transition-all shadow-xl rounded-sm"
              >
                {t('shared.articles.footer.cta_button')}
              </button>
            </div>
          </footer>
        </article>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-24 font-sans text-gray-900 animate-fade-in">
      {/* Editorial Hero */}
      <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-white border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gray-50 rounded-full blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-black pb-12">
              <div className="max-w-4xl">
                <div className="inline-block px-4 py-2 bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-[0.2em] mb-8 border border-gray-200">
                  {t('shared.articles.hero.badge')}
                </div>
                <h1 className="text-6xl md:text-8xl font-bold tracking-tighter leading-[0.85] mb-0">
                  {t('shared.articles.hero.title_part1')}<span className="text-clever-teal">.</span><br />
                  <span className="text-gray-400 uppercase">{t('shared.articles.hero.title_part2')}</span>
                </h1>
              </div>
              <div className="md:text-right font-mono text-[10px] uppercase tracking-[0.3em] text-gray-400 leading-relaxed">
                {t('shared.articles.hero.subtitle')}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Grid of Articles */}
      <section className="px-6 py-32 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-x-24 gap-y-32">
            {articles.map((art, idx) => (
              <Reveal key={art.id} delay={idx * 0.1}>
                <article onClick={() => handleSelectArticle(art.id)} className="group cursor-pointer">
                  <div className="aspect-video w-full bg-gray-100 mb-8 overflow-hidden rounded-sm grayscale group-hover:grayscale-0 transition-all duration-700 shadow-lg">
                    <img src={art.image} alt={art.title} className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-1000" />
                  </div>
                  <div className="border-t-4 border-black pt-10 mb-8 flex justify-between items-start">
                    <span className="text-7xl font-bold tracking-tighter font-mono opacity-5 group-hover:opacity-100 group-hover:text-clever-teal transition-all">
                      {art.number}
                    </span>
                    <span className="text-[10px] font-bold font-mono tracking-[0.3em] text-gray-400">{art.category}</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold tracking-tighter leading-tight mb-8 group-hover:text-clever-teal transition-colors">
                    {art.title}
                  </h3>
                  <p className="text-gray-500 mb-10 font-light leading-relaxed line-clamp-3 text-lg">
                    {art.excerpt}
                  </p>
                  <div className="flex items-center gap-4 border-b border-gray-100 pb-4 group-hover:border-black transition-colors">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-400 group-hover:text-black transition-colors">{t('shared.articles.item.read_more')}</span>
                    <ChevronRight size={14} className="text-clever-teal ml-auto" />
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription CTA - Bottom of Grid */}
      <section className="bg-rabbit-navy text-white py-32 px-6 overflow-hidden relative border-t border-white/5">
        <div className="absolute top-0 right-0 p-16 opacity-5 pointer-events-none">
          <svg width="400" height="400" viewBox="0 0 100 100" fill="white">
            <path d="M20 10 C 20 5 25 0 30 0 L 45 0 C 60 0 70 15 60 40 L 80 10 C 85 5 90 10 90 20 C 90 50 70 60 60 60 L 60 90 C 60 95 55 100 50 100 L 30 100 C 25 100 20 95 20 90 V 10 Z" />
          </svg>
        </div>
        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <Reveal>
            <h2 className="text-5xl md:text-8xl font-bold tracking-tighter mb-12 leading-[0.85]">
              {t('shared.articles.footer.bottom_hero').split(' ')[0]} <br /> <span className="text-clever-teal italic">{t('shared.articles.footer.bottom_hero').split(' ')[1]}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-xl mx-auto mb-12 font-light leading-relaxed">
              {t('shared.articles.footer.bottom_text')}
            </p>
            <form className="flex flex-col md:flex-row gap-0 max-w-xl mx-auto border border-gray-800 p-1 rounded-sm overflow-hidden" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder={t('shared.articles.footer.bottom_placeholder')}
                className="bg-transparent border-none py-4 px-6 text-white font-mono text-sm focus:outline-none flex-1 font-light"
              />
              <button className="bg-white text-black px-10 py-4 font-bold uppercase tracking-widest text-xs hover:bg-clever-teal hover:text-white transition-all whitespace-nowrap">{t('shared.articles.footer.bottom_cta')}</button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};
